import {TextField} from '@mui/material';
import React, {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import FirestoreManager from '../../api/firebaseManager';
import {CheckBox} from '../../components/Inputs';
import {useBridge} from '../../hooks/use-bridge';
import '../../styles/plaid.css';

export default function BridgePage({user}) {
  const navigate = useNavigate();
  const {item_id} = useParams();
  const {
    state: {connections},
    removeConnection,
    editConnection,
  } = useBridge();

  const connection = connections[item_id] || {};
  const {account_mappings, id, entity_mappings, custodian_mappings} =
    connection;

  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState('');

  const handleReconnect = async () => {
    if (!custodian_mappings || custodian_mappings.length === 0) {
      setReconnectError('No custodian mappings found for this connection');
      return;
    }

    setReconnecting(true);
    setReconnectError('');

    try {
      const existingById = {};
      if (account_mappings) {
        for (const acct of account_mappings) {
          existingById[acct.id] = acct;
        }
      }

      const advisor_codes = custodian_mappings.map(
        mapping => mapping.advisor_code || mapping.code,
      );
      const custodian =
        custodian_mappings[0].custodian || custodian_mappings[0].source;

      const response = await FirestoreManager.getBridgeAccounts({
        advisor_codes,
        custodian,
      });

      console.log(response);

      if (response?.success && Array.isArray(response.data)) {
        for (const account of response.data) {
          if (existingById[account.id]) continue;
          existingById[account.id] = {
            id: account.id,
            display_name: account.display_name,
            display_number: account.display_number,
            acct_type: account.acct_type,
            custodian: account.custodian,
            advisor_code: account.advisor_code,
            entity: connection.entity,
            ignore: false,
          };
        }
      }

      const mergedAccounts = Object.values(existingById);
      await editConnection({...connection, account_mappings: mergedAccounts});
    } catch (err) {
      console.log(err);
      setReconnectError('Error reconnecting accounts, try again later');
    } finally {
      setReconnecting(false);
    }
  };

  return (
    <div className='page-container'>
      <div className='flex-row justify-between'>
        <table>
          <thead>
            <tr>
              <th>Forest Entity Name</th>
              <th>Account Name</th>
              <th>Account Type</th>
              <th>Account Number</th>
              <th>Ignore</th>
            </tr>
          </thead>
          <tbody>
            {account_mappings?.length > 0 &&
              account_mappings.map(item => {
                return (
                  <Row
                    key={item.account_id}
                    item={item}
                    user={user}
                    connection={connection}
                  />
                );
              })}
          </tbody>
        </table>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            marginRight: '32px',
          }}
        >
          <button
            className='disconnect'
            onClick={() => {
              navigate('/dashboard');
            }}
          >
            Done
          </button>
          <button
            className='disconnect'
            disabled={reconnecting}
            onClick={handleReconnect}
          >
            {reconnecting ? 'Reconnecting...' : 'Reconnect Accounts'}
          </button>
          {reconnectError && (
            <span style={{color: 'red', fontSize: '14px'}}>
              {reconnectError}
            </span>
          )}
          <button
            className='disconnect'
            onClick={() => {
              try {
                removeConnection(connection);
                navigate('/dashboard');
              } catch (err) {
                console.log(err);
              }
            }}
          >
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}

const Row = ({item, user, connection}) => {
  const {editConnection} = useBridge();

  const {
    id,
    display_name,
    display_number,
    acct_type,
    custodian,
    advisor_code,
    entity,
  } = item;

  const [edit, setEdit] = useState(entity);

  return (
    <tr key={id}>
      <td>
        <TextField
          id='customerID'
          label='Forest Entity Name'
          placeholder='...'
          value={edit}
          type='text'
          size='small'
          onChange={evt => setEdit(evt.target.value)}
          onBlur={async () => {
            if (edit) {
              const accounts = connection.account_mappings.map(account => {
                if (account.id === id) {
                  return {...account, entity: edit.trim()};
                }
                return account;
              });
              await editConnection({...connection, account_mappings: accounts});
            } else {
              setEdit(entity);
            }
          }}
        />
      </td>
      <td>{display_name}</td>
      <td>{acct_type}</td>
      <td>{display_number || 'N/A'}</td>
      <td>
        <CheckBox
          value={item.ignore}
          setValue={async value => {
            const accounts = connection.account_mappings.map(account => {
              if (account.id === id) {
                return {...account, ignore: value};
              }
              return account;
            });
            await editConnection({...connection, account_mappings: accounts});
          }}
        />
      </td>
    </tr>
  );
};
